/**
 * useBreadcrumbLayers — deck.gl PathLayer replacement for DriverRenderer's dual Polylines.
 *
 * Returns the layers to pass to <DeckOverlay layers={...}> — three, plus an
 * optional leading halo layer on light basemaps:
 *
 *   [haloLayer?, visualLayer, arrowLayer, hitLayer]
 *
 *   haloLayer    Light-basemap only (isDarkBasemap=false): a thin dark casing drawn
 *                under the visual line for contrast. Omitted on dark basemaps.
 *
 *   visualLayer  Styled line: per-route colour, variable opacity (active/dimmed/base),
 *                variable width (normal = 3px, active/hovered = 5px).
 *                NOT pickable — prevents hover flicker on the visible line.
 *
 *   arrowLayer   Directional arrow icons sampled at regular intervals along each
 *                segment, replicating the `repeat: '50px'` behaviour of the DOM
 *                Polyline's FORWARD_CLOSED_ARROW icon symbol.
 *
 *   hitLayer     Transparent 20px-wide pickable line — the same wide-hit-area pattern
 *                as the existing invisible Polyline.  Fires onClick/onHover.
 *
 * Coordinate convention: breadcrumb segments use [lng, lat] (GeoJSON order).
 * deck.gl PathLayer expects positions as [longitude, latitude(, altitude)].
 */
import { IconLayer, PathLayer } from '@deck.gl/layers'
import { grey } from '@mui/material/colors'
import { useMemo, useRef } from 'react'

import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'
import { getArrowAtlas, sampleArrowPoints as sampleArrowPositions, arrowSpacingForZoom } from 'components/common/map/webgl/pathArrows'
import { COMMON } from 'theme/colors'

// Light-basemap halo: a thin dark casing under the breadcrumb recreates the
// contrast the dark basemap gives for free (mirrors useSequenceLineLayers).
const HALO_RGB = hexToRgb(COMMON.black)
const HALO_ALPHA = 64 // ~0.25 — subtle outline, additive to the existing line

// Wrap the shared `sampleArrowPositions` so each result also carries the
// per-route rgb tuple expected by this hook's IconLayer config.
function sampleArrowPoints(path, rgb, spacingM) {
  return sampleArrowPositions(path, spacingM).map(p => ({ ...p, rgb }))
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object}   params
 * @param {string[]} params.sortedRouteIds
 * @param {object}   params.breadcrumbsByRouteId  { routeId: [{ coordinates: [[lng,lat],...] }] }
 * @param {object}   params.routeLayers           { routeId: { routeColor: string } }
 * @param {object}   params.routesById            { routeId: { color: string } }
 * @param {string}   [params.activeRouteId]
 * @param {string}   [params.hoveredRouteId]
 * @param {function} [params.onRouteClick]   (routeId: string, info: object) => void
 * @param {function} [params.onRouteHover]   ({ routeId, coordinate }) => void
 * @param {function} [params.onRouteUnhover] () => void
 * @param {string}   [params.fallbackColor]  Default line colour when route has no colour set.
 * @param {number}   [params.zoom]           Current map zoom level; used to scale arrow spacing.
 * @param {boolean}  [params.isDarkBasemap]  When false, prepend a dark halo layer for contrast on light basemaps.
 * @returns {PathLayer[]}  [halo?, visual, arrows, hit] — halo present only on light basemaps.
 */
export function useBreadcrumbLayers({
  sortedRouteIds = [],
  breadcrumbsByRouteId = {},
  routeLayers = {},
  routesById = {},
  activeRouteId,
  hoveredRouteId,
  onRouteClick,
  onRouteHover,
  onRouteUnhover,
  fallbackColor = grey[500],
  zoom = 15,
  isDarkBasemap = false,
}) {
  // Flatten all route segments into a single data array.
  // Each entry carries the routeId and pre-parsed [lng, lat] path so deck.gl
  // doesn't have to re-parse strings on every render.
  const segments = useMemo(
    () =>
      sortedRouteIds.flatMap(routeId => {
        const segs = breadcrumbsByRouteId[routeId]
        if (!segs || !segs.length) return []
        const color = routeLayers[routeId]?.routeColor || routesById[routeId]?.color || fallbackColor
        return segs
          .filter(seg => seg.coordinates && seg.coordinates.length >= 2)
          .map(seg => ({
            routeId,
            path: seg.coordinates.map(c => [parseFloat(c[0]), parseFloat(c[1])]),
            rgb: hexToRgb(color),
          }))
      }),
    [sortedRouteIds, breadcrumbsByRouteId, routeLayers, routesById, fallbackColor]
  )

  // ── Halo layer — light-basemap dark casing under the visual line ──────────
  // Additive only: same path, a touch wider, drawn first so it reads as a thin
  // outline. Skipped on dark basemaps (the basemap already gives high contrast).
  const haloLayer = useMemo(() => {
    if (isDarkBasemap) return null
    return new PathLayer({
      id: 'breadcrumb-halo',
      data: segments,
      getPath: d => d.path,
      getColor: [...HALO_RGB, HALO_ALPHA],
      getWidth: d => (hoveredRouteId === d.routeId || activeRouteId === d.routeId ? 7 : 5),
      widthUnits: 'pixels',
      capRounded: true,
      jointRounded: true,
      pickable: false,
      updateTriggers: {
        getWidth: [activeRouteId, hoveredRouteId],
      },
    })
  }, [segments, isDarkBasemap, activeRouteId, hoveredRouteId])

  // ── Visual layer — styled, not pickable ──────────────────────────────────
  const visualLayer = useMemo(
    () =>
      new PathLayer({
        id: 'breadcrumb-visual',
        data: segments,
        getPath: d => d.path,
        getColor: d => {
          const [r, g, b] = d.rgb
          let alpha
          if (!activeRouteId) {
            alpha = 204 // 0.8 × 255
          } else if (activeRouteId === d.routeId) {
            alpha = 255 // full opacity
          } else {
            alpha = 38 // 0.15 × 255 — dimmed
          }
          return [r, g, b, alpha]
        },
        getWidth: d => (hoveredRouteId === d.routeId || activeRouteId === d.routeId ? 5 : 3),
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        pickable: false,
        updateTriggers: {
          getColor: [activeRouteId],
          getWidth: [activeRouteId, hoveredRouteId],
        },
      }),
    [segments, activeRouteId, hoveredRouteId]
  )

  // Arrow spacing — target ~50 screen-px gap, scaled by zoom. Sourced from
  // pathArrows.js so the breadcrumb and edit-mode WebGL paths use one cadence.
  const arrowSpacingM = useMemo(() => arrowSpacingForZoom(50, zoom), [zoom])

  // ── Arrow layer — directional arrows sampled at regular intervals ────────
  const arrowData = useMemo(() => segments.flatMap(seg => sampleArrowPoints(seg.path, seg.rgb, arrowSpacingM)), [segments, arrowSpacingM])

  const arrowLayer = useMemo(() => {
    const { iconAtlas, iconMapping } = getArrowAtlas()
    return new IconLayer({
      id: 'breadcrumb-arrows',
      data: arrowData,
      iconAtlas,
      iconMapping,
      getPosition: d => d.position,
      getIcon: 'arrow',
      getSize: 14,
      sizeUnits: 'pixels',
      sizeScale: 1,
      getAngle: d => d.angle,
      getColor: d => {
        const [r, g, b] = d.rgb
        return [r, g, b, 200]
      },
      pickable: false,
      updateTriggers: {
        getColor: [arrowData],
        getAngle: [arrowData],
      },
    })
  }, [arrowData])

  // The layer instance only rebuilds when `segments` changes, so callbacks are
  // read through a ref — otherwise a re-created handler never reaches deck.gl.
  const callbacksRef = useRef()
  callbacksRef.current = { onRouteClick, onRouteHover, onRouteUnhover }

  // ── Hit layer — transparent wide line for pointer events ─────────────────
  const hitLayer = useMemo(
    () =>
      new PathLayer({
        id: 'breadcrumb-hit',
        data: segments,
        getPath: d => d.path,
        getColor: [0, 0, 0, 0], // fully transparent
        getWidth: 20,
        widthUnits: 'pixels',
        pickable: true,
        onClick: info => info.object && callbacksRef.current.onRouteClick?.(info.object.routeId, info),
        onHover: ({ object, coordinate, x, y, srcEvent }) => {
          if (object) {
            // x,y are screen px — used by handlePolylineMouseMove for tooltip positioning
            callbacksRef.current.onRouteHover?.({ routeId: object.routeId, coordinate, x, y, srcEvent })
          } else {
            callbacksRef.current.onRouteUnhover?.()
          }
        },
      }),
    [segments]
  )

  return [haloLayer, visualLayer, arrowLayer, hitLayer].filter(Boolean)
}
